
import { Component, Watch } from 'vue-property-decorator'
import { Context } from '@nuxt/types'
import { Diagram } from '@interfaces/DiagramTypes'
import { LocaleMessages } from 'vue-i18n/types'
import { UserType, UserGameType } from '@interfaces/UserTypes'
import {
  getFrontpageUrl,
  getUserFullName,
  getUserJoinedAt,
  getUserLocationAsString,
} from '~/domain/helpers'
import { DesignersStoreActions, HireDesignerRequest } from '~/domain/interfaces'

import BasicDialog from '~/components/dialogs/BasicDialog.vue'

import MachinationsLightPage from '~/domain/pages/MachinationsLightPage'
import CommonInfiniteLoader from '~/components/common/CommonInfiniteLoader.vue'
@Component({
  name: 'ProfileView',
  layout: 'communityLayout',
  components: {
    BasicDialog,
    CommonInfiniteLoader,
  },
})
export default class ProfileView extends MachinationsLightPage {
  async asyncData(context: Context) {
    // get user
    const { store, route, error } = context

    await store.dispatch('common/setupPageLight', { context })

    const username = route.params.communityUser

    const user = await store.dispatch('designers/fetchDesigner', {
      context,
      username,
    })
    if (!user) {
      error({ statusCode: 404, message: 'Page not found' })
    }

    // get user's diagrams

    const diagrams = await store.dispatch('diagrams/getDiagramsForUser', {
      context,
      username,
      count: 50,
      page: 1,
    })

    const likedDiagrams = await store.dispatch('diagrams/getDiagramsForUser', {
      context,
      username,
      likedDiagrams: true,
    })

    // get followers and following

    const connections = await store.dispatch('designers/getUserConnections', {
      context,
      username,
    })

    // #todo remove this once change in app endpoint is changed, so that forHire param is always sent
    if (user.forHire === undefined) {
      await store.dispatch(DesignersStoreActions.FETCH_FOR_HIRE, {
        context,
        count: 100,
      })
    }

    return {
      user,
      diagrams: diagrams.list,
      likedDiagrams: likedDiagrams.list,
      following: connections.following,
      followers: connections.followers,
      totalDiagrams: diagrams.totalCount,
    }
  }

  private user: UserType | null | undefined = null
  public diagrams: Diagram[] | null = null
  public likedDiagrams: Diagram[] | null = null
  public following: UserType[]
  public followers: UserType[]

  public hireDesignerIsLoading: boolean = false
  public hireDesignerModalVisible: boolean = false
  public hireDesignerIsComplete: boolean = false

  public infiniteLoadingEnable: boolean = true
  public complete: boolean = false
  public loadingBtnLoading: boolean = false
  public loading: boolean = false
  public pageNo: number = 1
  public totalDiagrams: number = 0

  @Watch('hireDesignerModalVisible')
  onhireDesignerModalVisible(val: boolean, oldVal: boolean) {
    if (val) {
      this.modalData = { ...this.defaultModalData }
    }
  }

  public defaultModalData = {
    valid: true,
    emailRules: [
      (v: string): string | boolean =>
        /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
        'E-mail must be valid',
    ],
    nameRules: [
      (v: string): string | boolean => !!v || 'Required',
      (v: string): string | boolean =>
        /^[a-zA-Z\s]*$/.test(v) || 'Name must contain only letters',
    ],
    textRules: [(v: string): string | boolean => !!v || 'Required'],
    name: '',
    email: '',
    message: '',
    company: '',
    game: '',
  }

  public async getUserDiagrams() {
    const username = this.$route.params.communityUser
    let resultUserDiagrams = {} as any

    this.loading = true
    this.pageNo++

    resultUserDiagrams = await this.$store.dispatch(
      'diagrams/getDiagramsForUser',
      {
        context: this,
        username,
        count: 50,
        page: this.pageNo,
      }
    )

    this.totalDiagrams = resultUserDiagrams.totalCount
    this.diagrams = this.diagrams?.concat(resultUserDiagrams.list) || null

    this.loading = false
  }

  public modalData = this.defaultModalData

  get wpData() {
    return this.$store.state.community.wpData
  }

  get avatarSize(): number {
    return 120
  }

  get isDesignerForHire(): boolean {
    if (this.user?.forHire !== undefined) return !!this.user?.forHire
    else {
      const designerForHire = (
        this.$store.state.designers.forHire as Array<UserType>
      ).find((obj) => {
        return obj.username === this.user?.username
      })
      if (designerForHire) return true
      return false
    }
  }

  get userLocation(): string {
    return this.user ? getUserLocationAsString(this.user) : ''
  }

  get userJoinedAt(): string | LocaleMessages {
    if (this.user) {
      const joinedAt = getUserJoinedAt(this.user)
      return typeof joinedAt === 'string'
        ? this.$t(joinedAt)
        : this.$dayjs(joinedAt).format('MMMM YYYY')
    }
    return ''
  }

  get homeUrl(): string {
    return getFrontpageUrl()
  }

  get coverPhotoUrl(): string {
    return this.user && this.user.username
      ? `${this.$config.apiBaseURL}/user/photo/cover/${this.user.username}`
      : ''
  }

  private getUserName(user: UserType | null | undefined) {
    return getUserFullName(user)
  }

  private hasSocialLinks(): boolean {
    if (this.user && this.user.socials) {
      return (
        // Object.entries(this.user.socials).filter((link) => link).length > 0
        this.user.socials.facebook !== '' ||
        this.user.socials.twitter !== '' ||
        this.user.socials.linkedin !== ''
      )
    }
    return false
  }

  private hasValidGames(): boolean {
    if (this.user && this.user.games) {
      const validGames = this.user.games.filter(
        (g: UserGameType) => g.name || g.link
      )
      return validGames.length > 0
    }
    return false
  }

  // LATER: move in Getter:
  private accountEqualsAuthenticatedUser(): boolean {
    if (this.$store.$auth.user && this.user)
      return this.$store.$auth.user.username === this.user.username
    else return false
  }

  public async sendModalHireData(): Promise<void> {
    const valid = (this.$refs.hireForm as HTMLFormElement).validate()
    if (valid) {
      try {
        this.hireDesignerIsLoading = true

        if (this.user && this.user.email) {
          const params: HireDesignerRequest = {
            fullName: this.modalData?.name,
            email: this.modalData.email,
            company: this.modalData.company,
            projectName: this.modalData.game,
            designerUsername: this.user.username,
            message: this.modalData.message,
          }

          await this.$store.dispatch('designers/hireDesigner', {
            context: this,
            params,
          })
          this.hireDesignerIsLoading = false
          this.hireDesignerModalVisible = false
          this.hireDesignerIsComplete = true
        }
      } catch (error) {
        this.hireDesignerIsLoading = false
        this.hireDesignerIsComplete = false
      }
    }
  }

  private removeAsterix(text) {
    if (text.endsWith('*')) {
      return text.slice(0, -1)
    }

    return text
  }

  private extractFirstName(name) {
    const names = name.split(' ')

    if (names.length > 1) {
      return names[0]
    } else {
      return name.trim() === '' ? 'the user' : name
    }
  }

  private sanitize(s: string) {
    return s
      .replace('<', '&lt;')
      .replace('>', '&gt;')
      .replace('javascript:', '')
      .replace('"', '\\"')
  }

  head() {
    // @ts-ignore
    const pageHead = this.pageHead()
    return {
      ...pageHead,
      // @ts-ignore
      title: this.getUserName(this.user),
      titleTemplate: '%s • Machinations Community Profile',
      meta: [
        {
          hid: 'og:description',
          name: 'og:description',
          // @ts-ignore
          content: `Stay updated on ${this.getUserName(
            this.user
          )}'s contributions to the world of Machinations.`,
        },
        {
          hid: 'description',
          name: 'description',
          // @ts-ignore
          content: `Stay updated on ${this.getUserName(
            this.user
          )}'s contributions to the world of Machinations.`,
        },
        ...pageHead?.meta,
      ],
    }
  }
}
